/**
 * Adds a FIDO2 security key to the signed-in identity via the Kratos settings
 * flow. Kratos stores and verifies the credential; the backend only mirrors the
 * result for its own bookkeeping.
 */

import { client } from "../services/api";
import { LoginFlow, csrfToken, isRedirectAfterSuccess, messagesOf } from "./kratosLogin";
import {
  isUserCancellation,
  serializeAttestation,
  toCreationOptions,
  triggerOptions,
} from "./webauthn";

const KRATOS =
  process.env.REACT_APP_KRATOS_PUBLIC_URL || "http://localhost:4433";

export type EnrollResult =
  | { kind: "enrolled" }
  | { kind: "cancelled" }
  | { kind: "error"; message: string };

const REAUTH_REQUIRED =
  "For security, please sign in again before adding a security key.";

export async function enrollSecurityKey(displayName: string): Promise<EnrollResult> {
  const res = await fetch(`${KRATOS}/self-service/settings/browser`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    if (res.status === 401) return { kind: "error", message: "Sign in before adding a security key." };
    if (res.status === 403) return { kind: "error", message: REAUTH_REQUIRED };
    return { kind: "error", message: `Could not start account settings (HTTP ${res.status}).` };
  }
  const flow = (await res.json()) as LoginFlow;

  const options = triggerOptions(
    flow.ui.nodes.find((n) => n.attributes.name === "webauthn_register_trigger")
  );
  if (!options) {
    return { kind: "error", message: "Security keys are not enabled on the identity service." };
  }

  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.create({
      publicKey: toCreationOptions(options),
    })) as PublicKeyCredential | null;
  } catch (err) {
    if (isUserCancellation(err)) return { kind: "cancelled" };
    return {
      kind: "error",
      message: `Security key setup failed: ${err instanceof Error ? err.message : String(err)}`,
    };
  }
  if (!credential) return { kind: "cancelled" };

  const submit = await fetch(flow.ui.action, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      method: "webauthn",
      csrf_token: csrfToken(flow),
      webauthn_register: serializeAttestation(credential),
      webauthn_register_displayname: displayName,
    }),
  });

  if (!submit.ok) {
    const payload = (await submit.json().catch(() => null)) as
      | (Partial<LoginFlow> & { error?: { id?: string; message?: string } })
      | null;
    if (!isRedirectAfterSuccess(submit, payload)) {
      if (submit.status === 403 || payload?.error?.id === "session_refresh_required") {
        return { kind: "error", message: REAUTH_REQUIRED };
      }
      const failed = payload?.ui ? (payload as LoginFlow) : null;
      return {
        kind: "error",
        message:
          (failed && messagesOf(failed, "error")[0]) ||
          payload?.error?.message ||
          "Kratos rejected the security key.",
      };
    }
  }

  // Best effort: the backend keeps its own list for the admin UI.
  try {
    await client.post("/auth/fido/register/start", { display_name: displayName });
    await client.post("/auth/fido/register/complete", {});
  } catch {
    // Kratos already holds the credential; sign-in works without this mirror.
  }

  return { kind: "enrolled" };
}

export type PasswordChangeResult = { kind: "ok" } | { kind: "error"; message: string };

/**
 * Change password for signed-in user via Kratos settings flow.
 */
export async function changePassword(newPassword: string): Promise<PasswordChangeResult> {
  const res = await fetch(`${KRATOS}/self-service/settings/browser`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    if (res.status === 401) return { kind: "error", message: "Sign in before changing password." };
    if (res.status === 403) return { kind: "error", message: "For security, please sign in again." };
    return { kind: "error", message: `Could not start settings (HTTP ${res.status}).` };
  }
  const flow = (await res.json()) as LoginFlow;

  const submit = await fetch(flow.ui.action, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ method: "password", csrf_token: csrfToken(flow), password: newPassword }),
  });
  if (submit.ok) return { kind: "ok" };

  const payload = (await submit.json().catch(() => null)) as
    | (Partial<LoginFlow> & { error?: { id?: string; message?: string } })
    | null;
  if (isRedirectAfterSuccess(submit, payload)) return { kind: "ok" };
  if (submit.status === 403 || payload?.error?.id === "session_refresh_required") {
    return { kind: "error", message: "For security, please sign in again." };
  }
  const failed = payload?.ui ? (payload as LoginFlow) : null;
  return {
    kind: "error",
    message:
      (failed && messagesOf(failed, "error")[0]) ||
      payload?.error?.message ||
      "Could not save the new password.",
  };
}

/**
 * Sets a new password on an existing settings flow, e.g. the one Kratos hands
 * out after a successful account recovery.
 */
export async function setPasswordOnSettingsFlow(
  flowId: string,
  password: string
): Promise<PasswordChangeResult> {
  const res = await fetch(`${KRATOS}/self-service/settings/flows?id=${encodeURIComponent(flowId)}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    if (res.status === 403) return { kind: "error", message: REAUTH_REQUIRED };
    if (res.status === 410) return { kind: "error", message: "That reset link expired. Please start again." };
    return { kind: "error", message: `Could not load the password form (HTTP ${res.status}).` };
  }
  const flow = (await res.json()) as LoginFlow;

  const submit = await fetch(flow.ui.action, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ method: "password", csrf_token: csrfToken(flow), password }),
  });
  if (submit.ok) return { kind: "ok" };

  const payload = (await submit.json().catch(() => null)) as
    | (Partial<LoginFlow> & { error?: { id?: string; message?: string } })
    | null;
  if (isRedirectAfterSuccess(submit, payload)) return { kind: "ok" };
  if (submit.status === 403 || payload?.error?.id === "session_refresh_required") {
    return { kind: "error", message: REAUTH_REQUIRED };
  }
  const failed = payload?.ui ? (payload as LoginFlow) : null;
  return {
    kind: "error",
    message:
      (failed && messagesOf(failed, "error")[0]) ||
      payload?.error?.message ||
      "Could not save the new password.",
  };
}
